/**
 * Progress+ structured data (JSON-LD) — single source of truth.
 *
 * Design principles:
 * - Organization has a canonical @id ("https://progressplus.ca/#organization")
 *   and every other schema references it by @id rather than re-emitting.
 * - Same for LocalBusiness (@id "…/#localbusiness"), Person Kartik Thakkar
 *   (@id "…/about#kartik-thakkar"), and WebSite (@id "…/#website").
 * - All factories are pure and synchronous so they can be called during
 *   the initial React render pass — react-snap will capture them as static
 *   HTML, and non-JS crawlers see them on the first byte.
 * - No fabricated data. Reviews use the real Google Business Profile numbers
 *   (5.0 / 4 reviews). Pricing schema is intentionally omitted because
 *   /pricing has no visible prices.
 *
 * When you need to add new schemas, extend this file. Don't hardcode
 * <script type="application/ld+json"> anywhere else.
 */

import { CITIES } from "./cities";

export const SITE = "https://progressplus.ca";
export const ORG_ID = `${SITE}/#organization`;
export const BUSINESS_ID = `${SITE}/#localbusiness`;
export const WEBSITE_ID = `${SITE}/#website`;
export const KARTIK_ID = `${SITE}/about#kartik-thakkar`;
export const LOGO_URL = `${SITE}/logo-progress-plus-color.png`;
export const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_c5d6b1b7-13d1-439c-9b95-56a03c4b3a51/artifacts/hmghgwwg_ChatGPT%20Image%20Mar%2020%2C%202026%2C%2005_08_40%20AM.png";
export const PHONE = "+1-416-553-0838";
export const EMAIL = "kartik@progressplus.ca";
// Freshness signal for LLM crawlers — updates every build via react-snap SSG.
export const BUILD_DATE = new Date().toISOString().slice(0, 10);

const AREA_SERVED_CITIES = Object.entries(CITIES).map(([slug, c]) => ({
  "@type": "City",
  name: c.name || slug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()),
  containedInPlace: { "@type": "AdministrativeArea", name: "Ontario, Canada" },
}));

const SAME_AS = [
  "https://www.instagram.com/progressplus.ca",
  "https://www.facebook.com/share/18rR4KPRRR/",
];

/* ─────────── Organization (canonical, homepage-owned) ─────────── */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Progress+",
    alternateName: "Progress Plus",
    legalName: "Progress Plus Corp.",
    url: SITE,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
      width: 512,
      height: 512,
    },
    image: LOGO_URL,
    email: EMAIL,
    telephone: PHONE,
    foundingDate: "2024",
    founder: { "@id": KARTIK_ID },
    description:
      "Progress+ is Canada's concierge wellness brand — Registered Kinesiologist-supervised in-home personal training delivered across the Greater Toronto Area and Waterloo Region.",
    areaServed: "Greater Toronto Area, Waterloo Region, Ontario, Canada",
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE,
        email: EMAIL,
        contactType: "customer service",
        areaServed: "CA",
        availableLanguage: ["English", "Punjabi", "Hindi", "Gujarati", "Urdu"],
      },
    ],
    sameAs: SAME_AS,
  };
}

/* ─────────── LocalBusiness (homepage; sibling to Organization) ─────────── */
export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "HealthAndBeautyBusiness"],
    "@id": BUSINESS_ID,
    name: "Progress+",
    alternateName: "Progress Plus",
    legalName: "Progress Plus Corp.",
    url: SITE,
    image: LOGO_URL,
    logo: LOGO_URL,
    telephone: PHONE,
    email: EMAIL,
    priceRange: "$$$$",
    parentOrganization: { "@id": ORG_ID },
    address: {
      "@type": "PostalAddress",
      addressRegion: "ON",
      addressCountry: "CA",
    },
    areaServed: AREA_SERVED_CITIES,
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "08:00",
        closes: "23:00",
      },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: 4,
      bestRating: "5",
      worstRating: "1",
    },
    sameAs: SAME_AS,
    dateModified: BUILD_DATE,
  };
}

/* ─────────── WebSite (for SearchAction / sitelinks search box) ─────────── */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE,
    name: "Progress+",
    publisher: { "@id": ORG_ID },
    inLanguage: "en-CA",
    dateModified: BUILD_DATE,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE}/blog?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/* ─────────── Person: Kartik Thakkar ─────────── */
export function kartikSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": KARTIK_ID,
    name: "Kartik Thakkar",
    jobTitle: "Founder & CEO",
    email: EMAIL,
    image: FOUNDER_PHOTO,
    url: `${SITE}/about`,
    worksFor: { "@id": ORG_ID },
    description:
      "Kartik leads programming, practitioner standards, and brand direction at Progress+. His standard for what a Canadian wellness brand should look like — held to clinical practice, never lowered for convenience — is the standard every member experiences.",
    knowsAbout: [
      "Concierge wellness operations",
      "Regulated-profession service design",
      "Premium brand and practitioner standards",
      "Metabolic health programming",
      "South Asian health and longevity",
    ],
    sameAs: SAME_AS,
  };
}

/* ─────────── AboutPage wrapper ─────────── */
export function aboutPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${SITE}/about#page`,
    url: `${SITE}/about`,
    name: "About Progress+",
    isPartOf: { "@id": WEBSITE_ID },
    mainEntity: { "@id": ORG_ID },
    about: { "@id": KARTIK_ID },
  };
}

/* ─────────── ContactPage ─────────── */
export function contactPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${SITE}/contact#page`,
    url: `${SITE}/contact`,
    name: "Contact Progress+",
    isPartOf: { "@id": WEBSITE_ID },
    mainEntity: { "@id": ORG_ID },
  };
}

/* ─────────── BreadcrumbList (dynamic per route) ─────────── */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.path.startsWith("http") ? item.path : `${SITE}${item.path}`,
    })),
  };
}

/* ─────────── City LocalBusiness (per /locations/:city page) ─────────── */
export function cityLocalBusinessSchema(citySlug) {
  const c = CITIES[citySlug];
  if (!c) return null;
  const name = c.name || citySlug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const geo = c.lat && c.lng ? { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng } : undefined;
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "HealthAndBeautyBusiness"],
    "@id": `${SITE}/locations/${citySlug}#localbusiness`,
    name: `Progress+ — ${name}`,
    alternateName: "Progress Plus",
    parentOrganization: { "@id": ORG_ID },
    url: `${SITE}/locations/${citySlug}`,
    image: LOGO_URL,
    logo: LOGO_URL,
    telephone: PHONE,
    email: EMAIL,
    priceRange: "$$$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: name,
      addressRegion: "ON",
      addressCountry: "CA",
    },
    ...(geo ? { geo } : {}),
    areaServed: { "@type": "City", name, containedInPlace: { "@type": "AdministrativeArea", name: "Ontario, Canada" } },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "08:00",
        closes: "23:00",
      },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: 4,
      bestRating: "5",
      worstRating: "1",
    },
    sameAs: SAME_AS,
  };
}

/* ─────────── Service (per /programs/:slug page) ─────────── */
export function programServiceSchema(program) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE}/programs/${program.slug}#service`,
    name: program.name,
    serviceType: program.name,
    description: program.metaDescription || program.tagline || `Progress+ ${program.name} program — Fitness Specialist supervised, delivered to your home across the GTA and Waterloo Region.`,
    url: `${SITE}/programs/${program.slug}`,
    provider: { "@id": ORG_ID },
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Greater Toronto Area & Waterloo Region, Ontario, Canada",
    },
    audience: {
      "@type": "PeopleAudience",
      geographicArea: {
        "@type": "AdministrativeArea",
        name: "Ontario, Canada",
      },
    },
  };
}

/* ─────────── FAQPage (dynamic per page) ─────────── */
export function faqPageSchema(faqs, pageUrl) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": pageUrl ? `${pageUrl}#faq` : undefined,
    ...(pageUrl ? { url: pageUrl } : {}),
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q || f.question,
      acceptedAnswer: { "@type": "Answer", text: f.a || f.answer },
    })),
  };
}

/* ─────────── Blog (listing page) ─────────── */
export function blogSchema(posts) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${SITE}/blog#blog`,
    url: `${SITE}/blog`,
    name: "Progress+ Insights",
    description:
      "Progress+ Insights — Fitness Specialist-authored, evidence-based writing on training, women's health, senior fitness, metabolic health, and South Asian wellness.",
    publisher: { "@id": ORG_ID },
    inLanguage: "en-CA",
    ...(Array.isArray(posts) && posts.length
      ? {
          blogPost: posts.slice(0, 10).map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE}/blog/${p.slug}`,
            datePublished: p.date || p.publishedAt,
            image: p.image ? (p.image.startsWith("http") ? p.image : `${SITE}${p.image}`) : LOGO_URL,
          })),
        }
      : {}),
  };
}

/* ─────────── BlogPosting (single post) ─────────── */
export function blogPostingSchema(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${SITE}/blog/${post.slug}#article`,
    headline: post.title,
    description: post.excerpt || post.metaDescription || post.title,
    image: post.image ? (post.image.startsWith("http") ? post.image : `${SITE}${post.image}`) : LOGO_URL,
    datePublished: post.date || post.publishedAt,
    dateModified: post.updatedAt || post.date || post.publishedAt,
    author: post.author
      ? typeof post.author === "string"
        ? { "@type": "Person", name: post.author }
        : post.author
      : { "@type": "Organization", "@id": ORG_ID, name: "Progress+" },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${post.slug}` },
    inLanguage: "en-CA",
    url: `${SITE}/blog/${post.slug}`,
  };
}

/* ─────────── VideoObject (YouTube Shorts on home page) ───────────
 * Google requires: name, description, thumbnailUrl, uploadDate.
 * We use YouTube's canonical hqdefault thumbnail and mark contentUrl to
 * the /shorts/ URL so Video Search can surface it directly.
 */
export function youtubeShortSchema({ id, name, description, uploadDate, pageUrl }) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "@id": `${pageUrl || SITE}#video-${id}`,
    name,
    description,
    thumbnailUrl: [
      `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
    ],
    uploadDate,
    contentUrl: `https://www.youtube.com/shorts/${id}`,
    embedUrl: `https://www.youtube.com/embed/${id}`,
    publisher: { "@id": ORG_ID },
    ...(pageUrl ? { isPartOf: { "@type": "WebPage", url: pageUrl } } : {}),
  };
}

/* ─────────── City × Program service schema (LLM matrix pages) ───────────
 * Emits a Service block scoped to one city × one program combination.
 * Used by /pt/:city/:program routes to give ChatGPT/Perplexity a clean,
 * atomic entity to quote when someone asks "postnatal trainer in Vaughan".
 */
export function cityProgramServiceSchema({ programName, programSlug, cityName, citySlug, description, pageUrl }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    name: `${programName} in ${cityName} — Progress+`,
    description,
    url: pageUrl,
    provider: { "@id": ORG_ID },
    serviceType: programName,
    areaServed: {
      "@type": "City",
      name: cityName,
      containedInPlace: { "@type": "AdministrativeArea", name: "Ontario, Canada" },
    },
    audience: { "@type": "PeopleAudience", geographicArea: { "@type": "AdministrativeArea", name: cityName } },
    category: programSlug,
    dateModified: BUILD_DATE,
  };
}

/* ─────────── Aggregate rating (standalone, for Client Results) ─────────── */
export function aggregateRatingSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    itemReviewed: { "@id": ORG_ID },
    ratingValue: "5.0",
    reviewCount: 4,
    bestRating: "5",
    worstRating: "1",
  };
}
